/**
 * The Kautionen tab of the company overview: a management snapshot, not a second
 * deposits workspace.
 *
 * WHAT IT IS NOT
 * --------------
 * No tables, no search, no filters, no providers, no per-row states, no
 * drilldowns. Those live on `/admin/kautionen` and are reached through the link
 * in the quality card. Rebuilding them here would give the same question two
 * answers to drift apart.
 *
 * CLOSED MEANS SILENT
 * -------------------
 * `AdminUebersichtPage` mounts all five panels at once, so this component exists
 * from the first paint of a page nobody opened this tab on. `active` gates the
 * hook, so a closed tab issues no request at all, and the early return keeps it
 * from painting a row of em-dashes for data nobody asked for. That is the
 * pattern `FinanceTab` already uses on this page; the hook is still called
 * above the return because hooks run unconditionally.
 *
 * NO PERIOD, EVER
 * ---------------
 * The overview's period picker sits visibly above this tab, and these two
 * contracts have no period to give it: `tenancies` and `unit` record what a
 * deposit IS, not what it was. Nothing here reads `kpisPeriod`, `year` or
 * `month`, and changing the picker issues no deposit request. The
 * "Aktueller Stand" line is what tells a reader that — it names the backend's
 * Swiss business day, which is the only date these figures answer for.
 */
import React from "react";

import useDepositsData from "../../../pages/admin/deposits/useDepositsData";
import {
  selectDepositsAsOfDate,
  selectLandlordDepositKpis,
  selectTenantDepositKpis,
  selectTenantUnresolvedSummary,
} from "../../../pages/admin/deposits/depositsSelectors";
import DepositsTabKpis from "./DepositsTabKpis";
import DepositsTabQuality from "./DepositsTabQuality";
import DepositsTabSummary from "./DepositsTabSummary";

/**
 * ISO to DD.MM.YYYY, or null. The backend already stamped the Swiss business day
 * (Regel 51); this only rearranges it. Never `new Date()` — a reader in Sydney
 * must see the same day as one in Zurich.
 */
function formatAsOf(iso) {
  if (typeof iso !== "string") return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : null;
}

export default function DepositsTab({ active = true }) {
  const { tenant, landlord } = useDepositsData({ enabled: active });

  const tenantKpis = selectTenantDepositKpis(tenant.data);
  const landlordKpis = selectLandlordDepositKpis(landlord.data);
  const unresolved = selectTenantUnresolvedSummary(tenant.data);
  const asOf = formatAsOf(selectDepositsAsOfDate(tenant.data, landlord.data));

  // "Nothing here" only once a source has actually answered. A failed or pending
  // source is not an empty one, and saying so would report an outage as a fact
  // about the portfolio.
  const tenantEmpty =
    !tenant.loading && !tenant.error && (tenantKpis.total ?? 0) <= 0;
  const landlordEmpty =
    !landlord.loading && !landlord.error && (landlordKpis.total ?? 0) <= 0;
  const bothEmpty = tenantEmpty && landlordEmpty;

  if (!active) return null;

  return (
    <div className="space-y-4" data-testid="company-deposits-tab">
      <div>
        <h2 className="text-[15px] font-semibold leading-tight text-foreground">
          Kautionen &amp; Sicherheiten
        </h2>
        <p className="mt-0.5 text-[12.5px] leading-snug text-muted-foreground">
          Aktueller Überblick über Mieterkautionen und Sicherheitsleistungen bei
          Vermietern.
        </p>
        <p
          data-testid="deposits-tab-as-of"
          className="mt-0.5 text-[11px] text-muted-foreground"
        >
          {asOf ? `Aktueller Stand · ${asOf}` : "Aktueller Stand"}
        </p>
      </div>

      {bothEmpty ? (
        <p
          data-testid="deposits-tab-empty"
          className="rounded-xl border border-dashed border-border bg-card p-5 text-center text-[12.5px] text-muted-foreground"
        >
          Keine Kautionen oder Sicherheitsleistungen erfasst.
        </p>
      ) : (
        <>
          <DepositsTabKpis
            tenant={tenantKpis}
            landlord={landlordKpis}
            unresolved={unresolved}
            tenantLoading={tenant.loading}
            landlordLoading={landlord.loading}
            tenantError={tenant.error}
            landlordError={landlord.error}
            onRetryTenant={tenant.reload}
            onRetryLandlord={landlord.reload}
          />

          <DepositsTabSummary
            tenant={tenantKpis}
            landlord={landlordKpis}
            unresolved={unresolved}
            tenantLoading={tenant.loading}
            landlordLoading={landlord.loading}
            tenantError={tenant.error}
            landlordError={landlord.error}
            tenantEmpty={tenantEmpty}
            landlordEmpty={landlordEmpty}
            onRetryTenant={tenant.reload}
            onRetryLandlord={landlord.reload}
          />
        </>
      )}

      {/* Stays put when both sides are empty: the way to the detail page is the
          one thing an empty snapshot still owes the reader. */}
      <DepositsTabQuality
        tenantQuality={tenant.data?.data_quality}
        landlordQuality={landlord.data?.data_quality}
        tenantUnavailable={Boolean(tenant.error)}
        landlordUnavailable={Boolean(landlord.error)}
        loading={tenant.loading || landlord.loading}
      />
    </div>
  );
}
